import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 3-2: Underground with many brick structures and gaps
export function buildLevel3_2(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1;
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [14, 15],
    [30, 32],
    [48, 50],
    [66, 68],
    [84, 85],
    [102, 103],
    [120, 122],
    [138, 139],
    [156, 157]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };

  // early
  setTile(8, 4, TileType.Question, { kind: "mushroom" });

  setTile(18, 4, TileType.Brick);
  setTile(19, 4, TileType.Brick);
  setTile(20, 4, TileType.Question, { kind: "coin" });
  setTile(21, 4, TileType.Brick);

  // pipes
  const pipes: Array<[number, number]> = [
    [24, 2],
    [36, 3],
    [52, 4],
    [70, 2],
    [88, 3],
    [106, 4],
    [124, 2],
    [142, 3],
    [160, 4],
    [176, 2]
  ];
  for (const [px, h] of pipes) {
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe);
    }
  }

  // brick structures
  for (let x = 42; x <= 48; x++) setTile(x, 4, TileType.Brick);
  setTile(44, 4, TileType.Question, { kind: "coin" });
  setTile(46, 4, TileType.Question, { kind: "mushroom" });
  for (let x = 43; x <= 47; x++) setTile(x, 8, TileType.Brick);
  setTile(45, 8, TileType.Question, { kind: "coin" });

  for (let x = 60; x <= 66; x++) setTile(x, 4, TileType.Brick);
  setTile(62, 4, TileType.Question, { kind: "coin" });
  setTile(64, 4, TileType.Question, { kind: "coin" });
  for (let x = 61; x <= 65; x++) setTile(x, 8, TileType.Brick);
  setTile(63, 8, TileType.Question, { kind: "mushroom" });

  for (let x = 78; x <= 84; x++) setTile(x, 4, TileType.Brick);
  setTile(80, 4, TileType.Question, { kind: "coin" });
  setTile(82, 4, TileType.Question, { kind: "coin" });

  for (let x = 96; x <= 102; x++) setTile(x, 4, TileType.Brick);
  setTile(98, 4, TileType.Question, { kind: "mushroom" });
  setTile(100, 4, TileType.Question, { kind: "coin" });
  for (let x = 97; x <= 101; x++) setTile(x, 8, TileType.Brick);
  setTile(99, 8, TileType.Question, { kind: "coin" });

  for (let x = 114; x <= 120; x++) setTile(x, 4, TileType.Brick);
  setTile(116, 4, TileType.Question, { kind: "coin" });
  setTile(118, 4, TileType.Question, { kind: "coin" });

  for (let x = 132; x <= 138; x++) setTile(x, 4, TileType.Brick);
  setTile(134, 4, TileType.Question, { kind: "mushroom" });
  setTile(136, 4, TileType.Question, { kind: "coin" });

  for (let x = 148; x <= 154; x++) setTile(x, 4, TileType.Brick);
  setTile(150, 4, TileType.Question, { kind: "coin" });
  setTile(152, 4, TileType.Question, { kind: "coin" });

  setTile(164, 4, TileType.Brick);
  setTile(165, 4, TileType.Brick);
  setTile(166, 4, TileType.Brick);
  setTile(167, 4, TileType.Question, { kind: "coin" });

  // staircase
  stair(181, 1);
  stair(182, 2);
  stair(183, 3);
  stair(184, 4);
  stair(185, 4);
  stair(186, 4);
  stair(187, 4);
  stair(188, 3);
  stair(189, 2);
  stair(190, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block);

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 12 },
    { kind: "koopa", x: 18 },
    { kind: "goomba", x: 24 },
    { kind: "goomba", x: 30 },
    { kind: "koopa", x: 36 },
    { kind: "goomba", x: 42 },
    { kind: "goomba", x: 48 },
    { kind: "koopa", x: 54 },
    { kind: "goomba", x: 60 },
    { kind: "goomba", x: 66 },
    { kind: "koopa", x: 72 },
    { kind: "goomba", x: 78 },
    { kind: "goomba", x: 84 },
    { kind: "koopa", x: 90 },
    { kind: "goomba", x: 96 },
    { kind: "goomba", x: 102 },
    { kind: "koopa", x: 108 },
    { kind: "goomba", x: 114 },
    { kind: "goomba", x: 120 },
    { kind: "koopa", x: 126 },
    { kind: "goomba", x: 132 },
    { kind: "goomba", x: 138 },
    { kind: "koopa", x: 144 },
    { kind: "goomba", x: 150 },
    { kind: "goomba", x: 156 },
    { kind: "koopa", x: 162 },
    { kind: "goomba", x: 168 },
    { kind: "goomba", x: 174 },
    { kind: "koopa", x: 180 },
    { kind: "goomba", x: 186 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
